import React, { useState } from 'react';
import { firebaseStorage } from '../utils/firebase';
import { 
  Search, 
  ArrowRight, 
  Edit, 
  Save, 
  X, 
  Gift as GiftIcon,
  Image as ImageIcon,
  Moon,
  CheckCircle,
  AlertCircle,
  FileText
} from 'lucide-react';
import ImageUploadField from './ImageUploadField';

const CustomerEditPage = ({ onBack }) => {
  const [trackingCode, setTrackingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [gift, setGift] = useState(null);
  const [error, setError] = useState('');
  const [editingLantern, setEditingLantern] = useState(null);
  const [editingOpening, setEditingOpening] = useState(false);
  const [tempOpeningMessage, setTempOpeningMessage] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Temporary states for editing individual lantern
  const [tempMessage, setTempMessage] = useState('');
  const [tempImageURL, setTempImageURL] = useState('');
  const [tempDuaa, setTempDuaa] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingCode.trim()) return;

    setLoading(true);
    setError('');
    setGift(null);

    try {
      const foundGift = await firebaseStorage.trackGiftByCode(trackingCode.trim());
      if (foundGift) {
        // Check if approved or free
        if (foundGift.paymentStatus === 'approved' || foundGift.planType === 'free') {
          setGift(foundGift);
          setTempOpeningMessage(foundGift.openingMessage || '');
        } else {
          setError('عذراً، هذه الهدية لم يتم تفعيلها بعد. يرجى الانتظار حتى موافقة الإدارة.');
        }
      } else {
        setError('عفواً، لم نجد هدية بهذا الكود. تأكد من إدخال الكود الصحيح.');
      }
    } catch (err) {
      setError('حدث خطأ أثناء البحث، حاول مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLantern = (lantern) => {
    setEditingLantern(lantern.day);
    setTempMessage(lantern.message || '');
    setTempImageURL(lantern.imageURL || '');
    setTempDuaa(lantern.duaa || '');
  };

  const handleSaveLantern = async () => {
    if (!gift || editingLantern === null) return;

    const updatedLanterns = gift.lanterns.map(l => {
      if (l.day === editingLantern) {
        return {
          ...l,
          message: tempMessage,
          imageURL: tempImageURL,
          duaa: tempDuaa
        };
      }
      return l;
    });

    try {
      await firebaseStorage.updateGift(gift.id, { lanterns: updatedLanterns });
      setGift({ ...gift, lanterns: updatedLanterns });
      setEditingLantern(null);
      showSuccessMessage();
    } catch (err) {
      alert('حدث خطأ أثناء الحفظ. حاول مرة أخرى.');
    }
  };

  const handleSaveOpeningMessage = async () => {
    if (!gift) return;

    try {
      await firebaseStorage.updateGift(gift.id, { openingMessage: tempOpeningMessage });
      setGift({ ...gift, openingMessage: tempOpeningMessage });
      setEditingOpening(false);
      showSuccessMessage();
    } catch (err) {
      alert('حدث خطأ أثناء الحفظ. حاول مرة أخرى.');
    }
  };

  const showSuccessMessage = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const maxLanterns = gift?.planType === 'free' ? 5 : gift?.planType === 'basic' ? 15 : 30;
  const canEditImages = gift?.planType !== 'free';
  const canEditDuaa = gift?.planType === 'premium';

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white p-6 text-right" 
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button 
            onClick={onBack} 
            className="flex items-center gap-2 text-purple-300 hover:text-white transition-all"
          >
            <ArrowRight className="w-5 h-5" /> العودة للرئيسية
          </button>
          {saveSuccess && (
            <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-lg animate-in fade-in">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-300 font-bold">تم الحفظ بنجاح! ✅</span>
            </div>
          )}
        </div>

        {/* Search Section */}
        {!gift && (
          <div className="text-center mb-8">
            <Edit className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
            <h2 className="text-2xl font-bold mb-2">تعديل هديتك</h2>
            <p className="text-purple-200 text-sm">أدخل كود التتبع الخاص بك لتعديل الهدية</p>
          </div>
        )}

        {!gift ? (
          <form onSubmit={handleTrack} className="space-y-4 mb-8 max-w-md mx-auto">
            <input
              type="text"
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value)}
              placeholder="مثال: TS-1234"
              className="w-full px-4 py-4 rounded-xl bg-white/10 border border-white/20 text-white text-center focus:outline-none focus:border-yellow-400 text-lg font-mono"
            />
            <button
              type="submit"
              disabled={loading || !trackingCode}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-indigo-950 font-bold py-4 rounded-xl transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                'جاري البحث...'
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  ابحث عن هديتك
                </>
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-8">
            {/* Gift Info Header */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-yellow-400">إلى: {gift.recipientName}</h3>
                  <p className="text-purple-200">من: {gift.senderName}</p>
                </div>
                <div className="text-left">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                    gift.planType === 'premium' ? 'bg-yellow-500/20 text-yellow-300' :
                    gift.planType === 'basic' ? 'bg-blue-500/20 text-blue-300' :
                    'bg-gray-500/20 text-gray-300'
                  }`}>
                    {gift.planType === 'premium' ? 'بلس' : gift.planType === 'basic' ? 'بيزك' : 'مجاني'}
                  </span>
                </div>
              </div>
            </div>

            {/* Opening Message Section */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-yellow-400" />
                  رسالة الافتتاح
                </h4>
                {!editingOpening && (
                  <button
                    onClick={() => setEditingOpening(true)}
                    className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                )}
              </div>

              {editingOpening ? (
                <div className="space-y-4">
                  <textarea
                    value={tempOpeningMessage}
                    onChange={(e) => setTempOpeningMessage(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                    placeholder="رسالة الافتتاح..."
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveOpeningMessage}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all"
                    >
                      <Save className="w-5 h-5" />
                      حفظ
                    </button>
                    <button
                      onClick={() => {
                        setEditingOpening(false);
                        setTempOpeningMessage(gift.openingMessage || '');
                      }}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-lg transition-all"
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-lg leading-relaxed text-purple-100 whitespace-pre-wrap">
                  {gift.openingMessage || 'لا توجد رسالة افتتاحية'}
                </p>
              )}
            </div>

            {/* Lanterns Grid */}
            <div>
              <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                <GiftIcon className="w-5 h-5 text-yellow-400" />
                الهدايا اليومية ({maxLanterns} هدية)
              </h4>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {gift.lanterns.slice(0, maxLanterns).map((lantern) => (
                  <button
                    key={lantern.day}
                    onClick={() => handleEditLantern(lantern)}
                    className="aspect-square bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 hover:from-yellow-400/30 hover:to-yellow-600/30 rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-all border border-yellow-500/30 hover:border-yellow-400/50"
                  >
                    <GiftIcon className="w-8 h-8 text-yellow-300" />
                    <span className="text-2xl font-bold">{lantern.day}</span>
                    <span className="text-xs text-purple-200">
                      {lantern.message ? '✓ مكتوبة' : '⚠ فارغة'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 p-4 rounded-xl text-center text-red-200 max-w-md mx-auto">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            {error}
          </div>
        )}
      </div>

      {/* Edit Lantern Modal */}
      {editingLantern !== null && gift && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-yellow-400">
                تعديل هدية يوم {editingLantern}
              </h3>
              <button
                onClick={() => setEditingLantern(null)}
                className="p-2 rounded-full bg-white/10 hover:bg-red-500 transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Message */}
              <div>
                <label className="block text-sm mb-2 text-purple-200">
                  الرسالة
                </label>
                <textarea
                  value={tempMessage}
                  onChange={(e) => setTempMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                  placeholder="اكتب رسالتك الجميلة هنا..."
                />
              </div>

                {/* Image Upload (only for paid plans) */}
                {canEditImages && (
                  <div>
                    <label className="block text-sm mb-2 text-purple-200 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      صورة اليوم (اختياري)
                    </label>
                    <ImageUploadField
                      value={tempImageURL}
                      onChange={(url) => setTempImageURL(url)}
                      dayNumber={editingLantern}
                    />
                  </div>
                )}

              {/* Duaa (only for premium) */}
              {canEditDuaa && (
                <div>
                  <label className="block text-sm mb-2 text-purple-200 flex items-center gap-2">
                    <Moon className="w-4 h-4" />
                    دعاء (اختياري)
                  </label>
                  <textarea
                    value={tempDuaa}
                    onChange={(e) => setTempDuaa(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:border-yellow-400"
                    placeholder="دعاء جميل لهذا اليوم..."
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/20">
                <button
                  onClick={handleSaveLantern}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-xl"
                >
                  <Save className="w-5 h-5" />
                  حفظ التعديلات
                </button>
                <button
                  onClick={() => setEditingLantern(null)}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-4 rounded-lg transition-all"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerEditPage;
